import React, {
  ChangeEvent,
  ClipboardEvent as ReactClipboardEvent,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import {useNavigate} from 'react-router-dom'
import {toast} from 'react-toastify'
import {usePolicyChatDrawerStore} from '../../store/policyChatDrawerStore'
import {
  PolicyChatMessage,
  PolicyChatThread,
  usePolicyChat,
} from '../../pages/policyChat/hooks/usePolicyChat'
import {URL as BACKEND_URL} from '../../gql/client'

const drawerStyle: React.CSSProperties = {
  position: 'fixed',
  bottom: 24,
  right: 24,
  width: 380,
  maxWidth: 'calc(100vw - 48px)',
  height: 600,
  maxHeight: 'calc(100vh - 120px)',
  background: '#ffffff',
  borderRadius: 12,
  boxShadow: '0 12px 40px rgba(0,0,0,0.18), 0 2px 8px rgba(0,0,0,0.08)',
  zIndex: 1050,
  display: 'flex',
  flexDirection: 'column',
  overflow: 'hidden',
}

const headerStyle: React.CSSProperties = {
  padding: '12px 14px',
  borderBottom: '1px solid #e9ecef',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 8,
  flexShrink: 0,
}

const listStyle: React.CSSProperties = {
  flex: 1,
  minHeight: 0,
  overflowY: 'auto',
}

const messagesAreaStyle: React.CSSProperties = {
  flex: 1,
  minHeight: 0,
  overflowY: 'auto',
  padding: '12px',
  background: '#efeae2',
  display: 'flex',
  flexDirection: 'column',
  gap: 6,
}

const composerStyle: React.CSSProperties = {
  padding: '8px 10px',
  borderTop: '1px solid #e9ecef',
  display: 'flex',
  alignItems: 'flex-end',
  gap: 8,
  flexShrink: 0,
  background: '#fff',
}

const rowStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 12,
  padding: '12px 14px',
  borderBottom: '1px solid #f1f3f5',
  cursor: 'pointer',
  background: '#fff',
  border: 'none',
  width: '100%',
  textAlign: 'left',
}

const avatarStyle = (color: string, size = 40): React.CSSProperties => ({
  width: size,
  height: size,
  flexShrink: 0,
  borderRadius: '50%',
  background: color,
  color: '#fff',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontWeight: 700,
  fontSize: size > 36 ? '0.95rem' : '0.8rem',
})

const PALETTE = ['#0d6efd', '#198754', '#dc3545', '#fd7e14', '#6f42c1', '#20c997', '#0dcaf0']

const colorFor = (key: number | string) => {
  const text = String(key)
  let hash = 0
  for (let i = 0; i < text.length; i++) hash = (hash << 5) - hash + text.charCodeAt(i)
  return PALETTE[Math.abs(hash) % PALETTE.length]
}

const initials = (name: string) =>
  name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0].toUpperCase())
    .join('') || '?'

const formatRelativeTime = (iso: string | null | undefined) => {
  if (!iso) return ''
  const then = new Date(iso).getTime()
  if (Number.isNaN(then)) return ''
  const diffMs = Date.now() - then
  const diffMin = Math.floor(diffMs / 60000)
  if (diffMin < 1) return 'now'
  if (diffMin < 60) return `${diffMin}m`
  const diffH = Math.floor(diffMin / 60)
  if (diffH < 24) return `${diffH}h`
  const diffD = Math.floor(diffH / 24)
  if (diffD < 7) return `${diffD}d`
  return new Date(iso).toLocaleDateString()
}

const formatTime = (iso: string | null | undefined) => {
  if (!iso) return ''
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) return ''
  return date.toLocaleTimeString([], {hour: '2-digit', minute: '2-digit'})
}

const getSnippet = (thread: PolicyChatThread) => {
  const last = thread.last_message
  if (!last) return 'No messages yet'
  if (last.text && last.text.trim()) return last.text
  if (last.attachments && last.attachments.length > 0) {
    const first = last.attachments[0]
    if (first.attachment_type === 'IMAGE') return '📷 Photo'
    if (first.attachment_type === 'VIDEO') return '🎥 Video'
    if (first.attachment_type === 'AUDIO') return '🎙 Voice note'
    return '📎 File'
  }
  return ''
}

type Filter = 'all' | 'unread'

export const PolicyChatDrawer: React.FC = () => {
  const open = usePolicyChatDrawerStore((state) => state.open)
  const close = usePolicyChatDrawerStore((state) => state.close)

  if (!open) return null
  return <PolicyChatDrawerContent onClose={close} />
}

const PolicyChatDrawerContent: React.FC<{onClose: () => void}> = ({onClose}) => {
  const navigate = useNavigate()
  const {
    threads,
    loadingThreads,
    fetchThreads,
    selectedThread,
    selectThread,
    messages,
    loadingMessages,
    sendMessage,
    sendingMessage,
    token,
  } = usePolicyChat({autoSelectFirstThread: false})

  const [filter, setFilter] = useState<Filter>('all')
  const [query, setQuery] = useState('')
  const [draft, setDraft] = useState('')
  const [draftFiles, setDraftFiles] = useState<File[]>([])

  useEffect(() => {
    void fetchThreads()
  }, [fetchThreads])

  const filtered = useMemo(() => {
    const normalized = query.trim().toLowerCase()
    return threads.filter((thread) => {
      if (filter === 'unread' && thread.unread_count <= 0) return false
      if (!normalized) return true
      const haystack = [thread.display_name, thread.title, thread.last_message?.text || '']
        .join(' ')
        .toLowerCase()
      return haystack.includes(normalized)
    })
  }, [filter, query, threads])

  const inConversation = Boolean(selectedThread)

  const goToFullPage = () => {
    onClose()
    navigate(selectedThread ? `/policychat?thread=${selectedThread.id}` : '/policychat')
  }

  const goBackToList = () => {
    selectThread(null)
  }

  const handleOpenThread = (thread: PolicyChatThread) => {
    selectThread(thread)
    setDraft('')
    setDraftFiles([])
  }

  const handleSend = async () => {
    if (!selectedThread) return
    const hasText = draft.trim().length > 0
    const hasFiles = draftFiles.length > 0
    if (!hasText && !hasFiles) return
    const sent = await sendMessage(selectedThread.id, draft, draftFiles)
    if (sent) {
      setDraft('')
      setDraftFiles([])
    }
  }

  return (
    <div style={drawerStyle} role='dialog' aria-label='PolicyChat'>
      {inConversation && selectedThread ? (
        <ConversationView
          thread={selectedThread}
          messages={messages}
          loadingMessages={loadingMessages}
          draft={draft}
          setDraft={setDraft}
          draftFiles={draftFiles}
          setDraftFiles={setDraftFiles}
          onSend={handleSend}
          sending={sendingMessage}
          token={token}
          onBack={goBackToList}
          onExpand={goToFullPage}
          onClose={onClose}
        />
      ) : (
        <>
          <div style={headerStyle}>
            <div className='d-flex align-items-center gap-2'>
              <i className='bi bi-chat-heart-fill text-primary fs-4' />
              <strong>PolicyChat</strong>
            </div>
            <div className='d-flex align-items-center gap-1'>
              <button
                type='button'
                className='btn btn-sm btn-icon btn-light'
                title='Open full page'
                onClick={goToFullPage}
              >
                <i className='bi bi-arrows-angle-expand' />
              </button>
              <button
                type='button'
                className='btn btn-sm btn-icon btn-light'
                title='Close'
                onClick={onClose}
              >
                <i className='bi bi-x-lg' />
              </button>
            </div>
          </div>

          <div className='px-3 pt-2 pb-1' style={{flexShrink: 0}}>
            <input
              type='text'
              className='form-control form-control-sm'
              placeholder='Search chats…'
              value={query}
              onChange={(event) => setQuery(event.target.value)}
            />
          </div>

          <div className='d-flex gap-2 px-3 py-2 border-bottom' style={{flexShrink: 0}}>
            <button
              type='button'
              className={`btn btn-sm ${filter === 'all' ? 'btn-primary' : 'btn-light'}`}
              onClick={() => setFilter('all')}
            >
              All
            </button>
            <button
              type='button'
              className={`btn btn-sm ${filter === 'unread' ? 'btn-primary' : 'btn-light'}`}
              onClick={() => setFilter('unread')}
            >
              Unread
            </button>
          </div>

          <div style={listStyle}>
            {loadingThreads && threads.length === 0 && (
              <div className='text-muted text-center py-5'>Loading…</div>
            )}
            {!loadingThreads && filtered.length === 0 && (
              <div className='text-muted text-center py-5'>No chats to show.</div>
            )}
            {filtered.map((thread) => {
              const name = thread.display_name || thread.title || 'Chat'
              return (
                <button
                  key={thread.id}
                  type='button'
                  style={rowStyle}
                  onClick={() => handleOpenThread(thread)}
                  onMouseEnter={(event) => (event.currentTarget.style.background = '#f8f9fa')}
                  onMouseLeave={(event) => (event.currentTarget.style.background = '#fff')}
                >
                  <span style={avatarStyle(colorFor(thread.id), 44)}>{initials(name)}</span>
                  <div className='flex-grow-1 min-w-0'>
                    <div className='d-flex justify-content-between align-items-center gap-2'>
                      <span className='fw-bold text-truncate'>{name}</span>
                      <span className='text-muted fs-8 flex-shrink-0'>
                        {formatRelativeTime(thread.last_activity_at)}
                      </span>
                    </div>
                    <div className='d-flex align-items-center gap-2 mt-1'>
                      <span className='text-muted fs-8 text-truncate flex-grow-1'>
                        {getSnippet(thread)}
                      </span>
                      {thread.unread_count > 0 && (
                        <span className='badge badge-circle badge-primary flex-shrink-0'>
                          {thread.unread_count > 99 ? '99+' : thread.unread_count}
                        </span>
                      )}
                    </div>
                  </div>
                </button>
              )
            })}
          </div>
        </>
      )}
    </div>
  )
}

type ConversationViewProps = {
  thread: PolicyChatThread
  messages: PolicyChatMessage[]
  loadingMessages: boolean
  draft: string
  setDraft: (value: string) => void
  draftFiles: File[]
  setDraftFiles: React.Dispatch<React.SetStateAction<File[]>>
  onSend: () => Promise<void>
  sending: boolean
  token: string
  onBack: () => void
  onExpand: () => void
  onClose: () => void
}

const previewChipStyle: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 6,
  padding: '4px 8px',
  background: '#f1f3f5',
  borderRadius: 999,
  maxWidth: 200,
  fontSize: '0.75rem',
}

const formatBytes = (bytes: number) => {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

const formatSeconds = (seconds: number) => {
  const m = Math.floor(seconds / 60)
  const s = seconds % 60
  return `${m}:${String(s).padStart(2, '0')}`
}

const ConversationView: React.FC<ConversationViewProps> = ({
  thread,
  messages,
  loadingMessages,
  draft,
  setDraft,
  draftFiles,
  setDraftFiles,
  onSend,
  sending,
  token,
  onBack,
  onExpand,
  onClose,
}) => {
  const messagesRef = useRef<HTMLDivElement>(null)
  const initialScrollRef = useRef<number | null>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const lastMessageId = messages.length > 0 ? messages[messages.length - 1].id : null

  const [isRecording, setIsRecording] = useState(false)
  const [recordingSeconds, setRecordingSeconds] = useState(0)
  const mediaRecorderRef = useRef<MediaRecorder | null>(null)
  const recordingStreamRef = useRef<MediaStream | null>(null)
  const recordingChunksRef = useRef<Blob[]>([])
  const recordingTimerRef = useRef<ReturnType<typeof setInterval> | null>(null)

  useLayoutEffect(() => {
    if (!thread.id) return
    if (messages.length === 0) return
    if (messages[0].thread_id !== thread.id) return
    if (initialScrollRef.current === thread.id) return
    initialScrollRef.current = thread.id
    const scroll = () => {
      const el = messagesRef.current
      if (!el) return
      el.scrollTop = el.scrollHeight
    }
    scroll()
    requestAnimationFrame(scroll)
    const t1 = window.setTimeout(scroll, 100)
    return () => window.clearTimeout(t1)
  }, [messages, thread.id])

  useEffect(() => {
    if (lastMessageId === null) return
    if (initialScrollRef.current !== thread.id) return
    const el = messagesRef.current
    if (!el) return
    const nearBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 200
    if (nearBottom) el.scrollTo({top: el.scrollHeight, behavior: 'smooth'})
  }, [lastMessageId, thread.id])

  useEffect(() => {
    initialScrollRef.current = null
  }, [thread.id])

  const cleanupRecording = useCallback(() => {
    if (recordingTimerRef.current) {
      clearInterval(recordingTimerRef.current)
      recordingTimerRef.current = null
    }
    recordingStreamRef.current?.getTracks().forEach((track) => track.stop())
    recordingStreamRef.current = null
    mediaRecorderRef.current = null
    recordingChunksRef.current = []
    setIsRecording(false)
    setRecordingSeconds(0)
  }, [])

  const finishRecording = useCallback(
    (keep: boolean) =>
      new Promise<void>((resolve) => {
        const recorder = mediaRecorderRef.current
        if (!recorder) {
          cleanupRecording()
          resolve()
          return
        }
        const mimeType = recorder.mimeType || 'audio/webm'
        recorder.onstop = () => {
          const chunks = [...recordingChunksRef.current]
          cleanupRecording()
          if (keep && chunks.length > 0) {
            const blob = new Blob(chunks, {type: mimeType})
            const extension = mimeType.includes('ogg') ? 'ogg' : 'webm'
            const file = new File([blob], `voice-note-${Date.now()}.${extension}`, {type: mimeType})
            setDraftFiles((current) => [...current, file])
          }
          resolve()
        }
        recorder.stop()
      }),
    [cleanupRecording, setDraftFiles]
  )

  const startRecording = useCallback(async () => {
    if (!navigator.mediaDevices?.getUserMedia || typeof MediaRecorder === 'undefined') {
      toast.error('Voice recording is not supported in this browser')
      return
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({audio: true})
      const candidates = ['audio/webm;codecs=opus', 'audio/webm', 'audio/ogg;codecs=opus']
      const supported = candidates.find(
        (candidate) =>
          typeof MediaRecorder.isTypeSupported !== 'function' || MediaRecorder.isTypeSupported(candidate)
      )
      const recorder = supported
        ? new MediaRecorder(stream, {mimeType: supported})
        : new MediaRecorder(stream)
      recordingStreamRef.current = stream
      mediaRecorderRef.current = recorder
      recordingChunksRef.current = []
      recorder.ondataavailable = (event) => {
        if (event.data.size > 0) recordingChunksRef.current.push(event.data)
      }
      recorder.start()
      setIsRecording(true)
      setRecordingSeconds(0)
      recordingTimerRef.current = setInterval(() => {
        setRecordingSeconds((current) => current + 1)
      }, 1000)
    } catch {
      toast.error('Unable to access the microphone')
    }
  }, [])

  useEffect(
    () => () => {
      cleanupRecording()
    },
    [cleanupRecording]
  )

  useEffect(() => {
    if (mediaRecorderRef.current) {
      void finishRecording(false)
    }
  }, [finishRecording, thread.id])

  const handleKeyDown = (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault()
      void onSend()
    }
  }

  const handleAttachFiles = (event: ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files || [])
    if (files.length > 0) setDraftFiles((current) => [...current, ...files])
    event.target.value = ''
  }

  const handlePaste = (event: ReactClipboardEvent<HTMLTextAreaElement>) => {
    if (isRecording) return
    const items = event.clipboardData?.items
    if (!items) return
    const files: File[] = []
    for (let i = 0; i < items.length; i++) {
      if (items[i].kind === 'file') {
        const file = items[i].getAsFile()
        if (file) files.push(file)
      }
    }
    if (files.length === 0) return
    event.preventDefault()
    setDraftFiles((current) => [...current, ...files])
  }

  const removeDraftFile = (index: number) => {
    setDraftFiles((current) => current.filter((_, i) => i !== index))
  }

  const name = thread.display_name || thread.title || 'Chat'
  const hasPayload = Boolean(draft.trim() || draftFiles.length > 0)

  return (
    <>
      <div style={headerStyle}>
        <div className='d-flex align-items-center gap-2 flex-grow-1' style={{minWidth: 0}}>
          <button
            type='button'
            className='btn btn-sm btn-icon btn-light'
            title='Back'
            onClick={onBack}
          >
            <i className='bi bi-chevron-left' />
          </button>
          <span style={avatarStyle(colorFor(thread.id), 32)}>{initials(name)}</span>
          <div className='min-w-0'>
            <div className='fw-bold text-truncate'>{name}</div>
            <div className='text-muted fs-8 text-truncate'>
              {thread.thread_type === 'GROUP'
                ? `${thread.participant_count} participants`
                : formatRelativeTime(thread.last_activity_at)}
            </div>
          </div>
        </div>
        <div className='d-flex align-items-center gap-1 flex-shrink-0'>
          <button
            type='button'
            className='btn btn-sm btn-icon btn-light'
            title='Open full page'
            onClick={onExpand}
          >
            <i className='bi bi-arrows-angle-expand' />
          </button>
          <button
            type='button'
            className='btn btn-sm btn-icon btn-light'
            title='Close'
            onClick={onClose}
          >
            <i className='bi bi-x-lg' />
          </button>
        </div>
      </div>

      <div ref={messagesRef} style={messagesAreaStyle}>
        {loadingMessages && messages.length === 0 && (
          <div className='text-muted text-center py-5'>Loading messages…</div>
        )}
        {messages.map((message) => (
          <MessageBubble key={message.id} message={message} token={token} />
        ))}
      </div>

      {draftFiles.length > 0 && (
        <div
          className='d-flex flex-wrap gap-2 px-2 py-2 border-top'
          style={{flexShrink: 0, background: '#fafafa'}}
        >
          {draftFiles.map((file, index) => {
            const isImage = file.type.startsWith('image/')
            const isAudio = file.type.startsWith('audio/')
            const icon = isImage ? 'bi-image' : isAudio ? 'bi-mic' : 'bi-paperclip'
            return (
              <div key={`${file.name}-${index}`} style={previewChipStyle}>
                <i className={`bi ${icon}`} />
                <span className='text-truncate'>{file.name}</span>
                <span className='text-muted'>{formatBytes(file.size)}</span>
                <button
                  type='button'
                  className='btn btn-sm btn-icon btn-light p-0'
                  style={{width: 18, height: 18}}
                  onClick={() => removeDraftFile(index)}
                  title='Remove'
                >
                  <i className='bi bi-x' />
                </button>
              </div>
            )
          })}
        </div>
      )}

      <div style={composerStyle}>
        {isRecording ? (
          <div
            className='d-flex align-items-center gap-2 flex-grow-1 px-2 py-2 rounded'
            style={{background: '#fff5f5', color: '#c1121f'}}
          >
            <span
              style={{
                width: 8,
                height: 8,
                borderRadius: '50%',
                background: '#dc3545',
                animation: 'pulse 1s ease-in-out infinite',
              }}
            />
            <span className='fs-8 fw-semibold'>Recording</span>
            <span className='fs-8 ms-auto'>{formatSeconds(recordingSeconds)}</span>
            <button
              type='button'
              className='btn btn-sm btn-light'
              onClick={() => void finishRecording(false)}
              title='Cancel'
            >
              <i className='bi bi-trash' />
            </button>
            <button
              type='button'
              className='btn btn-sm btn-success'
              onClick={() => void finishRecording(true)}
              title='Stop'
            >
              <i className='bi bi-check-lg' />
            </button>
          </div>
        ) : (
          <>
            <button
              type='button'
              className='btn btn-sm btn-icon btn-light'
              onClick={() => fileInputRef.current?.click()}
              title='Attach file'
              disabled={sending}
            >
              <i className='bi bi-paperclip' />
            </button>
            <input
              ref={fileInputRef}
              type='file'
              multiple
              className='d-none'
              onChange={handleAttachFiles}
            />
            <textarea
              className='form-control form-control-sm'
              placeholder='Message…'
              rows={1}
              value={draft}
              onChange={(event) => setDraft(event.target.value)}
              onKeyDown={handleKeyDown}
              onPaste={handlePaste}
              style={{resize: 'none', maxHeight: 100}}
            />
            {hasPayload ? (
              <button
                type='button'
                className='btn btn-sm btn-primary'
                onClick={() => void onSend()}
                disabled={sending}
                title='Send'
              >
                <i className='bi bi-send-fill' />
              </button>
            ) : (
              <button
                type='button'
                className='btn btn-sm btn-icon btn-light'
                onClick={() => void startRecording()}
                title='Record voice note'
              >
                <i className='bi bi-mic' />
              </button>
            )}
          </>
        )}
      </div>
    </>
  )
}

const MessageBubble: React.FC<{message: PolicyChatMessage; token: string}> = ({message, token}) => {
  const isOwn = message.is_own
  const bubbleStyle: React.CSSProperties = {
    alignSelf: isOwn ? 'flex-end' : 'flex-start',
    maxWidth: '80%',
    background: isOwn ? '#d9fdd3' : '#ffffff',
    borderRadius: 10,
    padding: '6px 10px',
    boxShadow: '0 1px 0 rgba(17,27,33,0.06)',
    fontSize: '0.85rem',
    wordBreak: 'break-word',
  }

  return (
    <div style={bubbleStyle}>
      {!isOwn && message.sender && (
        <div className='fw-semibold fs-9 text-primary mb-1'>{message.sender.full_name}</div>
      )}
      {message.text && <div style={{whiteSpace: 'pre-wrap'}}>{message.text}</div>}
      {message.attachments?.map((attachment) => {
        const url = `${BACKEND_URL}${attachment.inline_url}${
          attachment.inline_url.includes('?') ? '&' : '?'
        }token=${encodeURIComponent(token)}`
        if (attachment.attachment_type === 'IMAGE') {
          return (
            <img
              key={attachment.id}
              src={url}
              alt={attachment.original_name}
              className='rounded mt-1'
              style={{maxWidth: '100%', display: 'block'}}
            />
          )
        }
        return (
          <div key={attachment.id} className='text-muted fs-8 mt-1'>
            <i className='bi bi-paperclip me-1' />
            {attachment.original_name}
          </div>
        )
      })}
      <div className='text-muted fs-9 text-end' style={{marginTop: 2}}>
        {formatTime(message.created_at)}
      </div>
    </div>
  )
}
